import { useState, useCallback, useEffect } from 'react';
import { getConversations } from '@bingads-webui-react/copilot-chat-history-data';
import { useChatHistoryPerfMarkers } from './use-chat-history-perf-markers';
import type {
  Conversation,
  RawConversation,
  UseUpdateConversationsProps,
  UseUpdateConversationsResult,
} from '../types';

// Normalize conversation objects to PascalCase to ensure consistent property access
function normalizeConversation(value: unknown): Conversation {
  const c = value as RawConversation;
  return {
    ConversationId: c.ConversationId ?? c.conversationId,
    Title: c.Title ?? c.title,
    LastInteractedTime: c.LastInteractedTime ?? c.lastInteractedTime,
  };
}

export const useUpdateConversations = ({
  uid, cid, logger, pageSize = 30, appPerfMarker, productName, getAuthToken,
}: UseUpdateConversationsProps): UseUpdateConversationsResult => {
  const { conversationsPerfMarker } = useChatHistoryPerfMarkers(appPerfMarker);
  const [conversations, setConversations] = useState([] as Conversation[]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasBeenCalled, setHasBeenCalled] = useState(false);

  const updateConversations = useCallback(async () => {
    if (!hasMoreItems) return;

    setHasError(false);
    setIsLoading(true);
    try {
      conversationsPerfMarker?.willInit();
      conversationsPerfMarker?.willFetchData();

      const isXandrProduct = productName === 'xandr_curate' || productName === 'xandr_monetization';
      const authToken = isXandrProduct && getAuthToken ? await getAuthToken() : undefined;

      const result = await getConversations({
        uid,
        cid,
        top: pageSize,
        skip,
        productName,
        authToken,
      });

      if (result.hasError) {
        const errorResult = result as {
          message?: string;
          error?: unknown;
        };
        setHasError(true);
        logger.error(`${errorResult.message} ${JSON.stringify(errorResult.error)}`);
        conversationsPerfMarker?.done();
        return;
      }

      const successResult = result as {
        data: {
          conversations: unknown[];
        };
      };
      const newConversations = successResult.data.conversations.map(normalizeConversation);
      if (newConversations.length < pageSize) {
        setHasMoreItems(false);
      }
      conversationsPerfMarker?.willRender();
      setConversations(prev => (skip === 0 ? newConversations : [...prev, ...newConversations]));
      conversationsPerfMarker?.done();
    } catch (error) {
      setHasError(true);
      logger.error(`Failed to fetch conversations: ${JSON.stringify(error)}`);
      conversationsPerfMarker?.done();
    } finally {
      setIsLoading(false);
      setHasBeenCalled(true);
    }
  }, [uid, cid, pageSize, skip, hasMoreItems, logger, conversationsPerfMarker, productName, getAuthToken]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMoreItems) {
      setSkip(prev => prev + pageSize);
    }
  }, [isLoading, hasMoreItems, pageSize]);

  useEffect(() => {
    updateConversations();
  }, [uid, cid, skip, updateConversations]);

  return {
    conversations,
    isLoading,
    hasMoreItems,
    hasError,
    setHasError,
    loadMore,
    setConversations,
    hasBeenCalled,
  };
};
